'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { SmartImage } from '../elements/smartimage';
import { Carousel } from '../structure/carousel';
import { ReviewSchema } from '../foundation/schema';
import { getGoogleReviewsByPlaceId } from './google.reviews.functions';
import { usePixelatedConfig } from '../config/config.client';
import './google.reviews.css';
/*
https://maps.googleapis.com/maps/api/place/textsearch/json?query=Manning+Metalworks+Morris+Plains+NJ&key=[API_KEY]
*/
/**
 * GoogleReviewsCard — Fetch and display Google Place reviews configured via pixelated config.
 *
 * @param {string} [props.language] - Optional language code to localize review text.
 * @param {number} [props.maxReviews] - Maximum number of reviews to display.
 */
GoogleReviewsCard.propTypes = {
    /** Language code for localization (optional) */
    language: PropTypes.string,
    /** Max number of reviews to display */
    maxReviews: PropTypes.number,
};
export function GoogleReviewsCard(props) {
    const config = usePixelatedConfig();
    const [place, setPlace] = useState();
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const placeId = config?.integrations?.googlePlaces?.placeId || '';
    const apiKey = config?.integrations?.googlePlaces?.apiKey || '';
    const proxyBase = config?.integrations?.global?.proxyUrl || undefined;
    useEffect(() => {
        if (!placeId) {
            setError('Place ID is required.');
            setLoading(false);
            return;
        }
        (async () => {
            try {
                const result = await getGoogleReviewsByPlaceId({
                    apiKey: apiKey,
                    placeId: placeId,
                    proxyBase: proxyBase,
                    language: props.language ?? undefined,
                    maxReviews: props.maxReviews ?? undefined,
                });
                setPlace(result.place);
                setReviews(result.reviews);
                setLoading(false);
            }
            catch (e) {
                const errorMessage = e?.message || 'Failed to fetch reviews';
                // Check for common CORS/network issues
                if (errorMessage.includes('Failed to fetch') || errorMessage.includes('CORS')) {
                    setError('Unable to load reviews. This may be due to CORS restrictions in the browser. Try using a proxy server or server-side rendering.');
                }
                else {
                    setError(errorMessage);
                }
                setLoading(false);
            }
        })();
    }, [apiKey, placeId, proxyBase, props.language, props.maxReviews]);
    if (loading) {
        return (_jsx("div", { className: "google-reviews-card", children: _jsx("p", { className: "loading", children: "Loading reviews..." }) }));
    }
    if (error) {
        return (_jsx("div", { className: "google-reviews-card", children: _jsxs("p", { className: "error", children: ["Error: ", error] }) }));
    }
    return (_jsxs("div", { className: "google-reviews-card", children: [_jsx("h3", { children: place?.name || 'Reviews' }), place?.formatted_address && (_jsx("p", { className: "address", children: place.formatted_address })), reviews.length === 0 ? (_jsx("p", { className: "no-reviews", children: "No reviews found." })) : (_jsx("ul", { children: reviews.map((r, i) => (_jsxs("li", { children: [_jsxs("div", { className: "review-header", children: [r.profile_photo_url && (_jsx("div", { className: "profile-photo-container", children: _jsx(SmartImage, { src: r.profile_photo_url, alt: r.author_name, className: "profile-photo" }) })), _jsxs("div", { children: [_jsx("div", { className: "author-name", children: r.author_name }), _jsxs("div", { className: "rating", children: ['★'.repeat(r.rating), '☆'.repeat(5 - r.rating), " ", r.rating, "/5", r.relative_time_description && _jsxs("span", { children: [" \u00B7 ", r.relative_time_description] })] })] })] }), r.text && _jsx("div", { className: "review-text", children: r.text })] }, i))) })), place && (_jsx("a", { href: `https://search.google.com/local/writereview?placeid=${place.place_id}`, target: "_blank", rel: "noopener noreferrer", className: "write-review", children: "Write a review on Google \u2192" }))] }));
}
/**
 * GoogleReviewsCarousel — Fetch and display Google Place reviews in a carousel or grid format.
 *
 * @param {string} [props.language] - Optional language code to localize review text.
 * @param {number} [props.maxReviews] - Maximum number of reviews to display.
 * @param {'carousel' | 'grid'} [props.displayMode] - Display mode: carousel or grid (default: carousel).
 * @param {boolean} [props.draggable] - Enable swipe/drag interactions on touch devices (default: false).
 * @param {'contain' | 'cover' | 'fill'} [props.imgFit] - Image fit mode for carousel cards (default: cover).
 * @param {string} [props.businessName] - Business name for review schema itemReviewed (default: "Local Business").
 * @param {boolean} [props.includeReviewSchema] - Include JSON-LD ReviewSchema for each loaded review (default: true).
 *
 * @returns {JSX.Element} A card component displaying Google Place reviews in the specified format.
 */
GoogleReviewsCarousel.propTypes = {
    /** Language code for localization */
    language: PropTypes.string,
    /** Max number of reviews to display */
    maxReviews: PropTypes.number,
    /** Display mode: carousel or grid */
    displayMode: PropTypes.oneOf(['carousel', 'grid']),
    /** Enable swipe/drag interactions on touch devices */
    draggable: PropTypes.bool,
    /** Image fit mode for carousel cards */
    imgFit: PropTypes.oneOf(['contain', 'cover', 'fill']),
    /** Business name for review schema itemReviewed */
    businessName: PropTypes.string,
    /** Include JSON-LD ReviewSchema for each loaded review */
    includeReviewSchema: PropTypes.bool,
};
export function GoogleReviewsCarousel(props) {
    const config = usePixelatedConfig();
    const [place, setPlace] = useState();
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const apiKey = config?.integrations?.googlePlaces?.apiKey || '';
    const placeId = config?.integrations?.googlePlaces?.placeId || '';
    const proxyBase = config?.integrations?.global?.proxyUrl || undefined;
    const displayMode = props.displayMode || 'carousel';
    const imgFit = props.imgFit || 'cover';
    const businessName = props.businessName || 'Local Business';
    const includeReviewSchema = props.includeReviewSchema !== false;
    useEffect(() => {
        if (!placeId) {
            setError('Place ID is required.');
            setLoading(false);
            return;
        }
        (async () => {
            try {
                const result = await getGoogleReviewsByPlaceId({
                    apiKey: apiKey,
                    placeId: placeId,
                    proxyBase: proxyBase,
                    language: props.language ?? undefined,
                    maxReviews: props.maxReviews ?? undefined,
                });
                setPlace(result.place);
                setReviews(result.reviews);
                setLoading(false);
            }
            catch (e) {
                const errorMessage = e?.message || 'Failed to fetch reviews';
                if (errorMessage.includes('Failed to fetch') || errorMessage.includes('CORS')) {
                    setError('Unable to load reviews. This may be due to CORS restrictions in the browser. Try using a proxy server or server-side rendering.');
                }
                else {
                    setError(errorMessage);
                }
                setLoading(false);
            }
        })();
    }, [apiKey, placeId, proxyBase, props.language, props.maxReviews]);
    if (loading) {
        return (_jsx("div", { className: "google-reviews-card", children: _jsx("p", { className: "loading", children: "Loading reviews..." }) }));
    }
    if (error) {
        return (_jsx("div", { className: "google-reviews-card", children: _jsxs("p", { className: "error", children: ["Error: ", error] }) }));
    }
    if (reviews.length === 0) {
        return (_jsx("div", { className: "google-reviews-card", children: _jsx("p", { className: "no-reviews", children: "No reviews found." }) }));
    }
    const cards = reviews.map((review, index) => ({
        index,
        cardIndex: index,
        cardLength: reviews.length,
        image: review.profile_photo_url || '',
        imageAlt: review.author_name,
        imgFit,
        headerText: `${review.rating}/5 Stars`,
        subHeaderText: review.relative_time_description,
        bodyText: review.text ? review.text : `- ${review.author_name}`,
    }));
    const aggregateRating = reviews.length > 0 ? {
        '@type': 'AggregateRating',
        ratingValue: (reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length).toFixed(1),
        reviewCount: reviews.length.toString(),
        ratingCount: reviews.length.toString(),
        bestRating: '5',
        worstRating: '1',
    } : undefined;
    const reviewSchemas = reviews.map((review) => ({
        '@context': 'https://schema.org/',
        '@type': 'Review',
        name: review.text ? review.text.substring(0, 110) : `${review.author_name} review`,
        reviewBody: review.text || '',
        datePublished: review.time ? new Date(review.time * 1000).toISOString() : undefined,
        author: {
            '@type': 'Person',
            name: review.author_name,
        },
        itemReviewed: {
            '@type': 'LocalBusiness',
            name: businessName,
            ...(aggregateRating && { aggregateRating }),
        },
        reviewRating: {
            '@type': 'Rating',
            ratingValue: review.rating.toString(),
            bestRating: '5',
            worstRating: '1',
        },
    }));
    return (_jsxs("div", { className: "google-reviews-card", children: [place?.name && _jsx("h3", { children: place.name }), place?.formatted_address && _jsx("p", { className: "address", children: place.formatted_address }), includeReviewSchema && reviewSchemas.map((schema, index) => (_jsx(ReviewSchema, { review: schema }, index))), displayMode === 'grid' ? (_jsx("div", { className: "google-reviews-grid", children: reviews.map((review, index) => (_jsxs("div", { className: "google-review-grid-card", children: [review.profile_photo_url && (_jsx("div", { className: "profile-photo-container", children: _jsx(SmartImage, { src: review.profile_photo_url, alt: review.author_name, className: "profile-photo" }) })), _jsxs("div", { children: [_jsx("div", { className: "author-name", children: review.author_name }), _jsxs("div", { className: "rating", children: ['★'.repeat(review.rating), '☆'.repeat(5 - review.rating), " ", review.rating, "/5"] }), review.relative_time_description && _jsx("div", { className: "review-time", children: review.relative_time_description }), review.text && _jsx("div", { className: "review-text", children: review.text })] })] }, index))) })) : (_jsx(Carousel, { cards: cards, draggable: props.draggable, imgFit: imgFit }))] }));
}
